﻿import { normalizeSearchText } from "@js/core/utils/search";
import { showToast } from "@js/components/common/toast";
import { t } from "@js/core/utils/i18n";

function getActivePanel() {
    return document.querySelector(".currency-panel.is-active");
}

async function copyText(text, successMessage, emptyMessage) {
    if (!text || !text.trim()) {
        showToast(emptyMessage || t("Copy.Empty"), { type: "error" });
        return;
    }

    try {
        await navigator.clipboard.writeText(text);
        showToast(successMessage || t("Copy.Done"));
    } catch {
        const tempInput = document.createElement("textarea");
        tempInput.value = text;
        document.body.appendChild(tempInput);
        tempInput.select();
        document.execCommand("copy");
        document.body.removeChild(tempInput);

        showToast(successMessage || t("Copy.Done"));
    }
}

function updateSearchClearVisibility(searchInput, clearSearchButton) {
    if (!searchInput || !clearSearchButton) return;

    const hasValue = searchInput.value.trim().length > 0;
    clearSearchButton.classList.toggle("is-visible", hasValue);
}

function updateEmptyState(panel) {
    if (!panel) return;

    const items = panel.querySelectorAll(".account-item");
    const emptyState = panel.querySelector(".accounts-empty-state");
    if (!emptyState) return;

    let visibleCount = 0;

    items.forEach((item) => {
        if (item.style.display !== "none") {
            visibleCount++;
        }
    });

    emptyState.classList.toggle("is-visible", visibleCount === 0);
}

function addCopyRipple(btn, event) {
    if (!btn) return;

    const oldRipple = btn.querySelector(".copy-ripple");
    if (oldRipple) oldRipple.remove();

    const rect = btn.getBoundingClientRect();
    const ripple = document.createElement("span");

    ripple.className = "copy-ripple";
    ripple.style.left = `${event.clientX - rect.left}px`;
    ripple.style.top = `${event.clientY - rect.top}px`;

    btn.appendChild(ripple);

    window.setTimeout(() => ripple.remove(), 650);
}

function setButtonSuccess(btn) {
    if (!btn) return;

    if (!btn._originalSvg) {
        btn._originalSvg = btn.innerHTML;
    }

    btn.innerHTML = `
        <svg viewBox="0 0 64 64" fill="none" aria-hidden="true">
            <circle cx="32" cy="32" r="28"
                stroke="currentColor"
                stroke-width="4"
                opacity="0.2"></circle>

            <path d="M18 33.5L27.5 43L46.5 22.5"
                stroke="currentColor"
                stroke-width="5"
                stroke-linecap="round"
                stroke-linejoin="round"
                style="
                    stroke-dasharray: 42;
                    stroke-dashoffset: 42;
                    animation: ibanCheckAnim 0.42s ease forwards;
                " />
        </svg>
    `;

    btn.classList.add("is-success");

    clearTimeout(btn._successTimer);
    btn._successTimer = window.setTimeout(() => {
        btn.innerHTML = btn._originalSvg;
        btn.classList.remove("is-success");
        btn._originalSvg = null;
    }, 3000);
}

function filterVisiblePanel(term) {
    const panel = getActivePanel();
    if (!panel) return;

    const items = panel.querySelectorAll(".account-item");
    const normalized = normalizeSearchText(term || "");

    items.forEach((item) => {
        const haystack = item.dataset.search || "";
        const normalizedHaystack = normalizeSearchText(haystack);

        const isMatch =
            !normalized ||
            normalizedHaystack.includes(normalized) ||
            normalizedHaystack.replace(/\s+/g, "").includes(normalized.replace(/\s+/g, ""));

        item.style.display = isMatch ? "" : "none";
    });

    updateEmptyState(panel);
}

function getVisibleIbans() {
    const panel = getActivePanel();
    if (!panel) return [];

    const visibleItems = Array.from(panel.querySelectorAll(".account-item"))
        .filter((item) => item.style.display !== "none");

    return visibleItems
        .map((item) => {
            const btn = item.querySelector(".copy-iban-btn");
            return btn ? (btn.dataset.iban || "").trim() : "";
        })
        .filter((x) => x && x !== "-");
}

function activateCurrency(currency, chips, panels, searchInput, clearSearchButton, printActiveCurrency) {
    chips.forEach((chip) => {
        chip.classList.toggle("is-active", chip.dataset.currencyTarget === currency);
    });

    panels.forEach((panel) => {
        panel.classList.toggle("is-active", panel.dataset.currencyPanel === currency);
    });

    if (printActiveCurrency) {
        const activeChip = document.querySelector(".currency-chip.is-active");
        if (activeChip) {
            const label = activeChip.querySelector("span");
            printActiveCurrency.textContent = label ? label.textContent.trim() : currency;
        } else {
            printActiveCurrency.textContent = currency;
        }
    }

    if (searchInput) {
        updateSearchClearVisibility(searchInput, clearSearchButton);
        filterVisiblePanel(searchInput.value);
    }
}

export async function initBankAccountsPage() {
    const chips = document.querySelectorAll(".currency-chip");
    const panels = document.querySelectorAll(".currency-panel");
    const searchInput = document.getElementById("bankSearchInput");
    const clearSearchButton = document.getElementById("clearBankSearch");
    const copyButtons = document.querySelectorAll(".copy-iban-btn");
    const copyVisibleIbansBtn = document.getElementById("copyVisibleIbansBtn");
    const printPdfBtn = document.getElementById("printPdfBtn");
    const toast = document.getElementById("copyToast");
    const printActiveCurrency = document.getElementById("printActiveCurrency");
    const emptyResetButtons = document.querySelectorAll('[data-clear-search="true"]');

    if (!chips.length && !panels.length) return;

    chips.forEach((chip) => {
        chip.addEventListener("click", function () {
            activateCurrency(
                this.dataset.currencyTarget,
                chips,
                panels,
                searchInput,
                clearSearchButton,
                printActiveCurrency
            );
        });
    });

    if (searchInput) {
        searchInput.addEventListener("input", function () {
            updateSearchClearVisibility(searchInput, clearSearchButton);
            filterVisiblePanel(this.value);
        });
    }

    if (clearSearchButton) {
        clearSearchButton.addEventListener("click", function () {
            if (!searchInput) return;

            searchInput.value = "";
            updateSearchClearVisibility(searchInput, clearSearchButton);
            filterVisiblePanel("");
            searchInput.focus();
        });
    }

    emptyResetButtons.forEach((btn) => {
        btn.addEventListener("click", function () {
            if (!searchInput) return;

            searchInput.value = "";
            updateSearchClearVisibility(searchInput, clearSearchButton);
            filterVisiblePanel("");
            searchInput.focus();
        });
    });

    copyButtons.forEach((btn) => {
        btn.addEventListener("click", async function () {
            addCopyRipple(this, event);
            const iban = this.dataset.iban || "";

            if (!iban || iban === "-") {
                showToast(t("BankAccounts.IbanEmpty"), { type: "error" });
                return;
            }

            await copyText(iban, t("BankAccounts.IbanCopied"), t("BankAccounts.IbanEmpty"));
            setButtonSuccess(btn);
        });
    });

    if (copyVisibleIbansBtn) {
        copyVisibleIbansBtn.addEventListener("click", async function () {
            const ibans = getVisibleIbans();

            if (!ibans.length) {
                showToast(t("BankAccounts.NoVisible"), { type: "error" });
                return;
            }

            await copyText(
                ibans.join("\n"),
                t("BankAccounts.IbansCopied", ibans.length),
                t("BankAccounts.IbanEmpty")
            );
        });
    }

    if (printPdfBtn) {
        printPdfBtn.addEventListener("click", function () {
            const activeChip = document.querySelector(".currency-chip.is-active");
            if (printActiveCurrency && activeChip) {
                const label = activeChip.querySelector("span");
                printActiveCurrency.textContent = label ? label.textContent.trim() : "USD";
            }

            window.print();
        });
    }

    activateCurrency(
        "USD",
        chips,
        panels,
        searchInput,
        clearSearchButton,
        printActiveCurrency
    );

    updateSearchClearVisibility(searchInput, clearSearchButton);

    const activePanel = getActivePanel();
    if (activePanel) {
        updateEmptyState(activePanel);
    }
}